"use client";

import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';

type ContentBlock = {
  type: 'paragraph' | 'quote' | 'subheading' | 'image';
  content?: string;
  src?: string;
  alt?: string;
  caption?: string;
  attribution?: string;
};

interface PressArticleContentProps {
  content: ContentBlock[];
}

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Container for all content blocks with animations
const AnimatedContentBlock = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    variants={fadeInUp}
    custom={delay}
    className="mb-4"
  >
    {children}
  </motion.div>
);

// Smaller components for different content types
const ParagraphBlock = ({ content }: { content: string }) => (
  <p className="text-[12px] leading-relaxed mb-4 font-mono">{content}</p>
);

const QuoteBlock = ({ content, attribution }: { content: string, attribution?: string }) => (
  <blockquote className="border-l-2 border-black pl-4 my-6">
    <p className="text-[12px] font-mono leading-relaxed mb-2 italic uppercase">{content}</p>
    {attribution && <p className="text-[10px] text-black/60 font-mono">— {attribution}</p>}
  </blockquote>
);

const SubheadingBlock = ({ content }: { content: string }) => (
  <h2 className="text-[14px] font-mono uppercase tracking-wide mt-8 mb-3 font-medium">{content}</h2>
);

// Image container component
const ImageContainer = ({ children }: { children: React.ReactNode }) => (
  <div className="relative w-full aspect-[16/9] overflow-hidden">
    {children}
  </div>
);

// Caption component
const ImageCaption = ({ caption }: { caption: string }) => (
  <figcaption className="text-[10px] text-black/60 mt-2 font-mono text-center">
    {caption}
  </figcaption>
);

// Image block component
const ImageBlock = ({ src, alt, caption }: { src: string, alt: string, caption?: string }) => (
  <figure className="my-6">
    <ImageContainer>
      <Image 
        src={src || ''} 
        alt={alt || ''} 
        fill 
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        className="object-cover grayscale hover:grayscale-0 transition-all duration-500"
      />
    </ImageContainer>
    {caption && <ImageCaption caption={caption} />}
  </figure>
);

// Specialized component for first paragraph to handle differently if needed
const LeadParagraphBlock = ({ content }: { content: string }) => (
  <p className="text-[13px] leading-relaxed mb-6 font-mono">{content}</p>
);

export const PressArticleContent = ({ content }: PressArticleContentProps) => {
  return (
    <motion.article 
      className="max-w-none"
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2,
          }
        }
      }}
    >
      {content.map((block, index) => {
        // Special handling for the first paragraph to make it stand out
        if (index === 0 && block.type === 'paragraph') {
          return (
            <AnimatedContentBlock key={index} delay={index * 0.1}>
              <LeadParagraphBlock content={block.content || ''} />
            </AnimatedContentBlock>
          );
        }
        
        switch (block.type) {
          case 'paragraph':
            return (
              <AnimatedContentBlock key={index} delay={index * 0.1}>
                <ParagraphBlock content={block.content || ''} />
              </AnimatedContentBlock>
            );
          case 'quote':
            return (
              <AnimatedContentBlock key={index} delay={index * 0.1}>
                <QuoteBlock content={block.content || ''} attribution={block.attribution} />
              </AnimatedContentBlock>
            );
          case 'subheading':
            return (
              <AnimatedContentBlock key={index} delay={index * 0.1}>
                <SubheadingBlock content={block.content || ''} />
              </AnimatedContentBlock>
            );
          case 'image':
            return (
              <AnimatedContentBlock key={index} delay={index * 0.1}>
                <ImageBlock 
                  src={block.src || ''}
                  alt={block.alt || ''}
                  caption={block.caption}
                />
              </AnimatedContentBlock>
            );
          default:
            return null;
        }
      })}
    </motion.article>
  );
}
